/**
 * @license Siftrock
 * (c) 2014-2018 Siftrock, Inc. http://siftrock.com
 * Unauthorized copying of this file, via any medium is strictly prohibited
 */

import { Component, OnInit } from '@angular/core';

import { CacheService } from '../global/ng2-cache/services/cache.service';
import { AdminService } from '../services/admin.service';
import { OpsService } from '../services/ops.service';
import { UtilService } from '../services/util.service';
import { SessionService } from '../services/session2.service';
import { ErrorService } from '../services/error.service';
import { Constants, AccountStatus } from '../global/constants';
import { GlobalMessageService } from '../services/global-message.service';

import { BsModalService } from 'ngx-bootstrap/modal';
import { BsModalRef } from 'ngx-bootstrap/modal/bs-modal-ref.service';

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html'
})
export class AdminComponent implements OnInit {

  loadingPage: boolean = true;
  addAccountDialogRef: BsModalRef;
  session: any = {};
  accounts: any[] = [];
  filteredAccounts: any[] = [];

  editions: any[] = ['All Editions'];

  newAccount: any = {
    company: '',
    domain: '',
    driftOrgId: null,
    isEmailBotsAccount: false,
  };
  exportData: string;

  constructor(private cacheService: CacheService,
              private adminService: AdminService,
              private opsService: OpsService,
              private sessionService: SessionService,
              private errorService: ErrorService,
              private utilService: UtilService,
              public constants: Constants,
              private modalService: BsModalService,
              private globalMessageService: GlobalMessageService) { }

  ngOnInit() {
    this.session = this.cacheService.get('session');
    if (!this.session) {
      this.sessionService.getSession(true).subscribe(
        (session: any) => {
          this.session = session;
          this.initView();
        }
      )
    }
    else {
      this.initView();
    }
  }

  filterSearch() {
    this.filterData();
    this.saveSession();
  }

  filterEdition() {
    this.filterData();
    this.saveSession();
  }

  filterStatus() {
    this.filterData();
    this.saveSession();
  }

  setSort(fieldName: string) {
    var direction = 1;
    if (this.session.admin_account_filters.sort.field == fieldName) {
      direction = (-1) * (this.session.admin_account_filters.sort.direction);
    }
    this.session.admin_account_filters.sort = {
      field: fieldName,
      direction: direction
    };

    this.filterData();
    this.saveSession();
  }

  addAccount(dialog) {
    this.addAccountDialogRef = this.modalService.show(dialog, {class: 'modal-md'});
  }

  createAccount() {
    this.adminService.createAccount(this.newAccount.company, this.newAccount.domain, this.newAccount.driftOrgId).subscribe(
      (result: any) => {
        this.globalMessageService.message('Account was added');
        this.initView();
      },
      err => {
        this.globalMessageService.error();
        this.errorService.log(err, 'settings-domains.component', 'newDomain');
      });
  }

  saveAccount() {
    this.addAccountDialogRef.hide();
    if (this.newAccount.isEmailBotsAccount) {
      const hasExistingDriftChatOrg = !!this.newAccount.driftOrgId
      const driftOrgId =  hasExistingDriftChatOrg ? this.newAccount.driftOrgId: -1 // if -1 is passed down, org is created
      const body = hasExistingDriftChatOrg ? {}: { orgName: this.newAccount.company, email: `email+admin@${this.newAccount.domain}` }

      return this.adminService.setupDriftOrg(body, driftOrgId).subscribe(
        (result: any) => {
          this.globalMessageService.message('Drift org was updated');
          this.newAccount.driftOrgId = result.orgId;
          this.createAccount()
        },
        err => {
          this.globalMessageService.error();
          this.errorService.log(err, 'settings-domains.component', 'driftSetup');
        }
      )
    }
    this.createAccount()
  }

  export() {
    this.loadingPage = true;
    this.adminService.adminExportAccounts().subscribe(
      (result: any) => {
        this.exportData = result._body;
        this.loadingPage = false;
      },
      err => {
        this.globalMessageService.error();
      }
    )
  }

  loadMeta(account: any) {
    account.detail = !account.detail;
    if (!account.meta) {
      account.loading = true;
      this.adminService.getAccountWithMeta(account._id).subscribe(
        (result: any) => {
          account.meta = result;
          account.loading = false;
        }
      );
    }
  }

  hubspotSync() {
    this.opsService.runHubpostSyncJob().subscribe(
      (result: any) => {
        this.globalMessageService.message(`Response from jobs: ${result}`)
      },
      err => {
        this.globalMessageService.error();
      }
    )
  }

  toggleIsEmailBotsAccount(newAccount) {
    this.newAccount.isEmailBotsAccount = newAccount.isEmailBotsAccount
  }

  private initView() {
    this.loadingPage = true;

    // make sure contact filters exist for this user
    if (!this.session.admin_account_filters) {
      this.resetFilters();
    }

    var now = new Date();
    var lastUpdate = new Date(this.session.admin_account_filters.last_changed);
    if (now.getTime() - lastUpdate.getTime() > 900000) {
      // 900000ms = 15 minutes. Let's reset filters if it's been more than 15 minutes since they were last last changed
      this.resetFilters();
    }

    this.adminService.getAccountsWithSKU().subscribe(
      (result: any) => {
        this.accounts = result;
        this.accounts.sort((a, b) => {
          if (a.created_at < b.created_at) {
            return 1;
          }
          else {
            return -1;
          }
        });

        this.accounts.forEach((account) => {
          account.detail = false;
          account.loading = false;
          this.editions.push(account.sku_name);
          account.integration_name = 'No Integration';
          if (account.integration.marketo) {
            account.integration_name = 'Marketo';
          }
          if (account.integration.eloqua) {
            account.integration_name = 'Eloqua';
          }
          if (account.integration.hubspot) {
            account.integration_name = 'HubSpot';
          }
          if (account.integration.acton) {
            account.integration_name = 'Act-On';
          }
          if (account.integration.pardot) {
            account.integration_name = 'Pardot';
          }

          account.valid_domain = 'No Domain';
          account.domains.forEach((d) => {
            if (d.verified) {
              account.valid_domain = d.domain;
            }
          });
        });

        this.editions = this.utilService.uniq(this.editions).sort();
        this.filterData();
        // console.log(this.accounts[0]);
        // // defer this call to after accounts have been loaded to make the UI faster
        // this.accounts.forEach(account => {
        //   this.adminService.getAccountWithMeta(account._id).subscribe(
        //     (result: any) => {
        //       account.meta = result;
        //       //console.log(account.meta);
        //     }
        //   )
        // });

      },
      error => {

      }
    );

    // this.adminService.getAccountsWithMeta().subscribe(
    //   (result: any) => {
    //     this.accounts = result;
    //     this.accounts.forEach((account) => {
    //       this.editions.push(account.sku_name);
    //       account.integration_name = 'No Integration';
    //       if (account.integration.marketo) {
    //         account.integration_name = 'Marketo';
    //       }
    //       if (account.integration.eloqua) {
    //         account.integration_name = 'Eloqua';
    //       }
    //       if (account.integration.hubspot) {
    //         account.integration_name = 'HubSpot';
    //       }
    //       if (account.integration.acton) {
    //         account.integration_name = 'Act-On';
    //       }

    //       account.valid_domain = 'No Domain';
    //       account.domains.forEach((d) => {
    //         if (d.verified) {
    //           account.valid_domain = d.domain;
    //         }
    //       });
    //     });

    //     this.editions = this.utilService.uniq(this.editions).sort();
    //     this.filterData();
    //   },
    //   error => {
    //     this.globalMessageService.error();
    //     this.errorService.log(error, 'admin.component', 'initView');
    //   }
    // )
  }


  private filterData() {
    this.loadingPage = true;
    this.filteredAccounts = [];
    this.accounts.forEach((account) => {
      var include = true;
      if (this.session.admin_account_filters.search.length > 0) {
        if (account.name.toLowerCase().indexOf(this.session.admin_account_filters.search) < 0) {
          include = false;
        }
      }

      if (this.session.admin_account_filters.sku_name != 'All Editions') {
        if (account.sku_name != this.session.admin_account_filters.sku_name) {
          include = false;
        }
      }

      if (!this.session.admin_account_filters.status) {
        this.session.admin_account_filters.status = 'active';
      }

      if (account.subscription.status != this.session.admin_account_filters.status) {
        include = false;
      }

      if (include) {
        this.filteredAccounts.push(account);
      }
    });

    var sort = this.session.admin_account_filters.sort;

    this.filteredAccounts.sort((a,b) => {
      if (a[sort.field] < b[sort.field]) {
        return (-1)*(sort.direction);
      }
      else {
        return sort.direction;
      }
    });

    this.loadingPage = false;
  }

  private saveSession() {
    this.session.admin_account_filters.last_changed = new Date();
    this.sessionService.saveSession(this.session).subscribe(
      (result: any) => {
        // Nothing
      },
      error => {
        this.globalMessageService.error();
        this.errorService.log(error, 'admin.component', 'saveSession');
      }
    )
  }

  private resetFilters() {
    this.session.admin_account_filters = {
      search: '',
      sku_name: 'All Editions',
      active: true,
      sort: {
        field: 'created_at',
        direction: -1
      }
    };

    this.saveSession();
  }
}



// WEBPACK FOOTER //
// ./client/app/admin/admin.component.ts